// AF-3.16 — Learning Center. AF-2.8 (operational) and AF-3.8 (creator)
// learnings are sourced and displayed SEPARATELY -- never merged into a
// single undifferentiated list (spec Section 20). Conflicting learnings
// (same metric, opposing real outcome) are flagged explicitly, never
// resolved by majority vote.

import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { BrandContextBar } from '@/components/brand/BrandContextBar';
import { LearningCard, LearningConflictNotice } from '@/components/brand/LearningCard';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { useBrandContext } from '@/hooks/useBrandContext';
import { brandGrowthAPI } from '@/lib/intelligenceApi';

export default function LearningCenterPage() {
  const ctx = useBrandContext();
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!ctx.ready) return;
    setLoading(true); setError(null);
    brandGrowthAPI.learnings(ctx.workspaceId, ctx.entityType, ctx.entityId)
      .then((r) => setView(r.data))
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [ctx.ready, ctx.workspaceId, ctx.entityType, ctx.entityId]);

  useEffect(() => { load(); }, [load]);

  const af28 = view?.af28_learnings || [];
  const af38 = view?.af38_learnings || [];
  const conflictingIds = new Set(view?.conflicting_learning_ids || []);
  const totalConflicting = conflictingIds.size;

  return (
    <DashboardLayout>
      <div data-testid="learning-center-page" className="max-w-5xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Learning Center</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">What we measured, what happened, what we learned, and what remains unknown.</p>
        </div>

        <BrandContextBar ctx={ctx} />

        {loading && <LoadingState />}
        {error && <ErrorState error={error} onRetry={load} />}

        {!loading && !error && af28.length === 0 && af38.length === 0 && (
          <EmptyState title="NO LEARNINGS YET" description="Martian has not generated any learnings for this entity yet -- learnings are produced only after a measured outcome exists." />
        )}

        {!loading && !error && (af28.length > 0 || af38.length > 0) && (
          <>
            <LearningConflictNotice count={totalConflicting} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div data-testid="af28-learnings-column">
                <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">Operational learnings (AF-2.8)</p>
                <div className="space-y-2">
                  {af28.length === 0
                    ? <p className="text-[11px] text-[var(--text-tertiary)]">None yet.</p>
                    : af28.map((l) => <LearningCard key={l.id} learning={l} source="operational" conflicting={conflictingIds.has(l.id)} />)}
                </div>
              </div>
              <div data-testid="af38-learnings-column">
                <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-2">Creator learnings (AF-3.8)</p>
                <div className="space-y-2">
                  {af38.length === 0
                    ? <p className="text-[11px] text-[var(--text-tertiary)]">None yet.</p>
                    : af38.map((l) => <LearningCard key={l.id} learning={l} source="creator" conflicting={conflictingIds.has(l.id)} />)}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
